import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarPlus, ShieldCheck, Wrench, LifeBuoy } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore, type Asset } from '../../../store/assetStore';
import { useAssetSettingsStore } from '../../../store/assetSettingsStore';
import { useTicketStore } from '../../../store/ticketStore';

type WarrantyState = 'active' | 'expiring' | 'expired' | 'none';
const BADGE: Record<WarrantyState, { label: string; tone: 'success' | 'warning' | 'destructive' | 'muted' }> = {
  active: { label: 'Active', tone: 'success' },
  expiring: { label: 'Expiring soon', tone: 'warning' },
  expired: { label: 'Expired', tone: 'destructive' },
  none: { label: 'No warranty date', tone: 'muted' },
};

/** Whole days from today (local calendar) to a 'YYYY-MM-DD' date. */
function daysUntil(date: string) {
  if (!date) return 0;
  const clean = date.includes('T') ? date.slice(0, 10) : date;
  const [y, m, d] = clean.split('-').map(Number);
  const target = new Date(y, m - 1, d).getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((target - now.getTime()) / 86_400_000);
}

/**
 * Warranty tracker — warranty dates from assets.assets, alert window from
 * Asset settings (warranty.warranty_alert_threshold_days). Extending saves a
 * new warranty end date; a claim is logged as a 'Warranty claim' service job.
 */
export function WarrantyTrackerView() {
  const { assets, maintenance, fetchAssets, fetchMaintenance, updateAsset, createMaintenance, isLoading } = useAssetStore();
  const { settings, fetchSettings } = useAssetSettingsStore();
  const { tickets, fetchTickets } = useTicketStore();
  const [filter, setFilter] = useState<'attention' | 'all'>('attention');
  const [extending, setExtending] = useState<{ id: string; date: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    fetchAssets();
    fetchMaintenance();
    fetchSettings();
    fetchTickets(undefined, true);
  }, [fetchAssets, fetchMaintenance, fetchSettings, fetchTickets]);

  const alertDays = settings?.warranty.warranty_alert_threshold_days ?? 60;
  const stateOf = (a: Asset): WarrantyState => {
    if (!a.warrantyUntil) return 'none';
    const d = daysUntil(a.warrantyUntil);
    if (d < 0) return 'expired';
    return d <= alertDays ? 'expiring' : 'active';
  };

  const tracked = useMemo(
    () => assets.filter((a) => a.status !== 'Retired' && a.status !== 'Disposed')
      .map((a) => ({ asset: a, state: stateOf(a), days: a.warrantyUntil ? daysUntil(a.warrantyUntil) : null }))
      .sort((x, y) => (x.days ?? 1e9) - (y.days ?? 1e9)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assets, alertDays]
  );
  const count = (s: WarrantyState) => tracked.filter((t) => t.state === s).length;
  const rows = filter === 'attention' ? tracked.filter((t) => t.state === 'expiring' || t.state === 'expired') : tracked;

  const getLinkedTickets = (assetId: string, assetCode: string) => {
    const list: { ticketNo: string; type?: string; status?: string }[] = [];
    const seen = new Set<string>();

    maintenance.forEach((m) => {
      const matches = String(m.assetId) === String(assetId) || m.assetCode === assetCode;
      const ref = m.ticketNo || m.ticket_no || m.ticketId || (m as any).ticket_id;
      if (matches && ref && !seen.has(String(ref))) {
        seen.add(String(ref));
        list.push({ ticketNo: String(ref), type: m.maintenanceType, status: m.status });
      }
    });

    tickets.forEach((t) => {
      const matches =
        (t.primaryAssetId && String(t.primaryAssetId) === String(assetId)) ||
        (t.primaryAssetCode && t.primaryAssetCode === assetCode) ||
        (t.assetTag && t.assetTag === assetCode) ||
        (t.assetId && String(t.assetId) === String(assetId));
      const ref = t.ticketNumber || t.ticketId;
      if (matches && ref && !seen.has(String(ref))) {
        seen.add(String(ref));
        list.push({ ticketNo: String(ref), type: t.type, status: t.status });
      }
    });

    return list;
  };

  const saveExtension = async () => {
    if (!extending?.date) return;
    setBusyId(extending.id);
    try {
      const a = await updateAsset(extending.id, { warrantyUntil: extending.date });
      toast.success(`${a.asset_code} warranty now ends ${a.warrantyUntil}`);
      setExtending(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const logClaim = async (a: Asset) => {
    setBusyId(a.id);
    try {
      const res = await createMaintenance({
        assetId: a.id,
        maintenance_type: 'Warranty claim',
        scheduled_date: new Date().toISOString().slice(0, 10),
        supplier_id: a.supplierId ?? null,
        description: `Warranty claim for ${a.name} [${a.asset_code}]${a.warrantyUntil ? ` (warranty until ${a.warrantyUntil})` : ''}`,
      });
      const ticketRef = (res as any)?.ticketNo || (res as any)?.ticket_no || (res as any)?.ticketId;
      if (ticketRef) {
        toast.success(`Warranty claim logged: Ticket ${ticketRef} opened in Support Desk`);
      } else {
        toast.success(`Warranty claim logged for ${a.asset_code} — track it in Service schedule`);
      }
      fetchMaintenance();
      fetchTickets(undefined, true);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader className="mb-0" icon={ShieldCheck} title="Warranty tracker" description={`Warranties ending within ${alertDays} days are flagged (change this in Asset settings).`} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(['active', 'expiring', 'expired', 'none'] as WarrantyState[]).map((s) => (
          <Card key={s} className="p-4">
            <p className="text-xs text-muted-foreground">{BADGE[s].label}</p>
            <p className={`mt-2 font-display text-metric ${s === 'expired' && count(s) ? 'text-destructive-strong' : s === 'expiring' && count(s) ? 'text-warning-strong' : 'text-foreground'}`}>{count(s)}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><ShieldCheck className="size-4" /> Warranties ({rows.length})</h2>
          <div className="inline-flex rounded-lg bg-secondary p-0.5 text-xs">
            {(['attention', 'all'] as const).map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)}
                className={`h-7 rounded-md px-3 font-medium cursor-pointer ${filter === f ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                {f === 'attention' ? 'Needs attention' : 'All'}
              </button>
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <EmptyState className="m-4 border-0" title={isLoading ? 'Loading…' : filter === 'attention' ? 'Nothing needs attention' : 'No assets registered yet'} description="Assets with warranties ending soon or already expired appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Asset</th>
                  <th className="px-4 py-2 font-semibold">Vendor</th>
                  <th className="px-4 py-2 font-semibold">Warranty</th>
                  <th className="px-4 py-2 font-semibold">Linked Ticket</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map(({ asset: a, state, days }) => {
                  const linkedTickets = getLinkedTickets(a.id, a.asset_code);
                  return (
                    <tr key={a.id} className="hover:bg-secondary/30">
                      <td className="px-4 py-2">
                        <p className="font-medium text-foreground">{a.asset_code}</p>
                        <p className="text-xs text-muted-foreground">{a.name}</p>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{a.vendor || '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {a.warrantyStart || a.warrantyUntil ? `${a.warrantyStart ? (a.warrantyStart.includes('T') ? a.warrantyStart.slice(0, 10) : a.warrantyStart) : '—'} → ${a.warrantyUntil ? (a.warrantyUntil.includes('T') ? a.warrantyUntil.slice(0, 10) : a.warrantyUntil) : '—'}` : '—'}
                        {days !== null && <p className="text-xs font-mono">{days < 0 ? `${-days} days ago` : `${days} days left`}</p>}
                      </td>
                      <td className="px-4 py-2">
                        {linkedTickets.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {linkedTickets.map((t) => (
                              <a
                                key={t.ticketNo}
                                href={`/support/tickets?ticketId=${encodeURIComponent(t.ticketNo)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded bg-info/10 px-2 py-0.5 font-mono text-xs font-semibold text-info-strong border border-info/20 hover:bg-info/20 transition-colors"
                                title={`${t.type || 'Support Ticket'} · Status: ${t.status || 'Active'}`}
                              >
                                <LifeBuoy className="size-3 text-info" />
                                <span>{t.ticketNo}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2"><Badge variant={BADGE[state].tone}>{BADGE[state].label}</Badge></td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        {extending?.id === a.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <input type="date" className="h-8 rounded-md border border-input bg-background px-2 text-sm" value={extending.date}
                              onChange={(e) => setExtending({ id: a.id, date: e.target.value })} />
                            <Button size="xs" onClick={saveExtension} isLoading={busyId === a.id} disabled={!extending.date}>Save</Button>
                            <Button size="xs" variant="ghost" onClick={() => setExtending(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button size="xs" variant="ghost" onClick={() => setExtending({ id: a.id, date: a.warrantyUntil ? (a.warrantyUntil.includes('T') ? a.warrantyUntil.slice(0, 10) : a.warrantyUntil) : '' })}><CalendarPlus /> Extend</Button>
                            {state !== 'none' && state !== 'expired' && (
                              <Button size="xs" variant="ghost" onClick={() => logClaim(a)} disabled={busyId === a.id}><Wrench /> Log claim</Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
